import { put, select, call, all, race, take } from 'redux-saga/effects'
import { pickBy, identity } from 'lodash'

import { departureTimeSelector } from 'reduxModules/selectors/BookSelectors'
import { departurePlaceSelector } from 'reduxModules/selectors/CitySelectors'
import { mergedSourcesSelector } from 'reduxModules/selectors/SourceSelectors'
import {
  systemSelector,
  lastnameSelector,
  firstnameSelector,
  phoneSelector,
  priceSelector,
  addPaxSelector,
  noteSelector,
  toSelector,
  fromSelector,
  limitSelector,
  placeSelector
} from 'reduxModules/selectors/BookFormSelectors'
import {
  routeIdSelector,
  routeNameSelector,
  routeTypeSelector,
  totalSheetBookingsSelector,
  fromUaRouteSelector
} from 'reduxModules/selectors/RouteSelectors'
import { dateSelector } from 'reduxModules/selectors/DateSelectors'
import { addDatesSelector } from 'reduxModules/selectors/CitySelectors'
import {
  userRoleSelector,
  isPartnerSelector,
  isLimitedDispatcherSelector,
  userIdSelector,
  userNameSelector,
  companySelector
} from 'reduxModules/selectors/UserSelectors'
import { createDateId, addDays } from './helpers'
import { modalActions, modalTypes } from 'reduxModules/actions/ModalActions'
import { createReadableDateTime, createReadableDate } from './helpers'

import { rsf, firebase } from '../firebase'

export function* createBooking() {
  const date = yield select(dateSelector)
  const system = yield select(systemSelector)
  const place = yield select(placeSelector)

  const surname = yield select(lastnameSelector)
  const name = yield select(firstnameSelector)
  const phone = yield select(phoneSelector)
  const price = yield select(priceSelector)
  const addPax = yield select(addPaxSelector)

  const selectNote = yield select(noteSelector)
  const to = yield select(toSelector)
  const from = yield select(fromSelector)

  const routeId = yield select(routeIdSelector)
  const routeName = yield select(routeNameSelector)
  const routeType = yield select(routeTypeSelector)

  const userRole = yield select(userRoleSelector)
  const isPartner = yield select(isPartnerSelector)
  const isLimitedDispatcher = yield select(isLimitedDispatcherSelector)
  const userId = yield select(userIdSelector)
  const userName = yield select(userNameSelector)
  const company = yield select(companySelector)

  const departurePlace = yield select(departurePlaceSelector)
  const departureTime = yield select(departureTimeSelector)
  const departureDate = createReadableDate(date)
  const departureTimestamp = date.valueOf()
  const createdDate = createReadableDateTime(new Date())
  const daysToAdd = yield select(addDatesSelector)
  const ticketDepartureDate = createReadableDate(addDays(date, daysToAdd))

  const source = system
    ? system
    : userRole
  const sourceId = system
  const note = isPartner || isLimitedDispatcher
    ? `${userName.toUpperCase()} | ${selectNote || ''}`
    : selectNote

  const bookData = {
    from: from.trim(),
    to: to.trim(),
    routeId,
    routeName,
    routeType,
    userRole,
    userId,
    userName,
    departurePlace,
    departureTime: departureTime[routeId],
    departureDate,
    departureTimestamp,
    createdDate,
    source,
    sourceId,
    note,
    place,
    company,
    ticketDepartureDate
  }
  const filteredBookData = pickBy(bookData, identity)

  const passengerData = {
    surname,
    name,
    phone: phone.length === 9 ? `48${phone}` : phone,
    price
  }

  const booking = {
    ...filteredBookData,
    ...passengerData
  }

  const bookings = [booking]

  if (addPax) {
    addPax.forEach(({ phone, ...other }) => {
      bookings.push({
        ...filteredBookData,
        ...other,
        phone: phone.length === 9 ? `48${phone}` : phone,
      })
    })
  }

  return bookings
}

export function* createSourceBooking(systemLabel, systemId = null, diff = null, d = null) {
  const date = d ? d : yield select(dateSelector)
  const system = yield systemId || select(systemSelector)
  const place = yield select(placeSelector)

  const routeId = yield select(routeIdSelector)
  const routeName = yield select(routeNameSelector)
  const routeType = yield select(routeTypeSelector)

  const surname = yield select(lastnameSelector)
  const name = yield select(firstnameSelector)
  const phone = yield select(phoneSelector)
  const price = yield select(priceSelector)
  const addPax = yield select(addPaxSelector)

  const note = yield systemLabel.toUpperCase() || select(noteSelector)
  const to = yield select(toSelector)
  let from = yield select(fromSelector)

  const userRole = yield select(userRoleSelector)
  const userId = yield select(userIdSelector)
  const userName = yield select(userNameSelector)
  const company = yield select(companySelector)

  const departurePlace = yield select(departurePlaceSelector)
  const departureTime = yield select(departureTimeSelector)
  const departureDate = createReadableDate(date)
  const departureTimestamp = date.valueOf()
  const createdDate = createReadableDateTime(new Date())
  const daysToAdd = yield select(addDatesSelector)
  const ticketDepartureDate = createReadableDate(addDays(date, daysToAdd))

  const source = system ? 'system' : userRole
  const sourceId = system

  const splitedSourceLabel = systemLabel.split(' ')

  const fromUaRoute = yield select(fromUaRouteSelector)

  if (splitedSourceLabel[1] === 'АВ' && fromUaRoute) {
    from = splitedSourceLabel[0] === 'ІВ'
      ? 'Івано-Франківськ'
      : splitedSourceLabel[0]
  } else if (splitedSourceLabel[0] === 'Полонус') {
    from = 'Варшава'
  }

  const bookData = {
    from: from ? from.trim() : '',
    to: to ? to.trim() : '',
    routeId,
    routeName,
    routeType,
    userRole,
    userId,
    userName,
    departurePlace,
    departureTime: departureTime[routeId],
    departureDate,
    departureTimestamp,
    createdDate,
    source,
    sourceId,
    note,
    place,
    company,
    ticketDepartureDate
  }

  const filteredBookData = pickBy(bookData, identity)

  const passengerData = {
    surname,
    name,
    phone: phone && phone.length === 9 ? `0048${phone}` : phone,
    price
  }

  const booking = {
    ...filteredBookData,
    ...passengerData
  }

  const filteredBooking = pickBy(booking, identity)

  const bookings = [filteredBooking]

  if (diff) {
    for (let i = 1; i < diff; i++) {
      bookings.push(filteredBooking)
    }
  } else if (addPax) {
    addPax.map(pax => bookings.push({
      ...filteredBookData,
      ...pax
    }))
  }

  return bookings
}

export function* handleRouteLimit(value) {
  const date = yield select(dateSelector)
  const dateId = createDateId(date)
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const routeId = yield select(routeIdSelector)

  return yield call(
    rsf.firestore.setDocument,
    `routes/${routeId}/info/${dateId}`,
    {
      month,
      year,
      limit: value,
    },
    { merge: true }
  )
}

export function* updateTotalSheetBookings() {
  const date = yield select(dateSelector)
  const routeId = yield select(routeIdSelector)
  const totalSheetBookings = yield select(totalSheetBookingsSelector)
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const dateId = createDateId(date)

  return yield call(
    rsf.firestore.setDocument,
    `routes/${routeId}/info/${dateId}`,
    {
      month,
      year,
      totalSheetBookings: totalSheetBookings - 1,
    },
    { merge: true }
  )
}

export function* handleRouteInfo(booking) {
  const date = yield select(dateSelector)
  const totalSheetBookings = yield select(totalSheetBookingsSelector)
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const limit = yield select(limitSelector)
  const routeId = yield select(routeIdSelector)
  const dateId = createDateId(date)
  const updatedLimit = limit - booking.length

  return yield call(
    rsf.firestore.setDocument,
    `routes/${routeId}/info/${dateId}`,
    {
      month,
      year,
      limit: updatedLimit < 0 ? 0 : updatedLimit,
      totalSheetBookings: totalSheetBookings
        ? totalSheetBookings + booking.length
        : booking.length,
    },
    { merge: true }
  )
}

export function* addBookedPlace(place) {
  if (!place) {
    return
  }

  const date = yield select(dateSelector)
  const routeId = yield select(routeIdSelector)
  const dateId = createDateId(date)

  return yield call(
    rsf.firestore.setDocument,
    `routes/${routeId}/info/${dateId}`,
    {
      lastModified: firebase.firestore.FieldValue.serverTimestamp(),
      bookedPlaces: firebase.firestore.FieldValue.arrayUnion(place)
    },
    { merge: true }
  )
}

export function* removeBookedPlace(place) {
  if (!place) {
    return
  }

  const date = yield select(dateSelector)
  const routeId = yield select(routeIdSelector)
  const dateId = createDateId(date)

  return yield call(
    rsf.firestore.setDocument,
    `routes/${routeId}/info/${dateId}`,
    {
      lastModified: firebase.firestore.FieldValue.serverTimestamp(),
      bookedPlaces: firebase.firestore.FieldValue.arrayRemove(place)
    },
    { merge: true }
  )
}

export function* handleSourceData() {
  const date = yield select(dateSelector)
  const routeId = yield select(routeIdSelector)
  const mergedSources = yield select(mergedSourcesSelector)
  const month = date.getMonth() + 1
  const year = date.getFullYear()
  const dateId = createDateId(date)

  return yield call(
    rsf.firestore.setDocument,
    `routes/${routeId}/info/${dateId}`,
    {
      month,
      year,
      sourcesData: mergedSources
    },
    { merge: true }
  )
}

export function* handleBooking(booking, data) {
  const isPartner = yield select(isPartnerSelector)

  if (isPartner) {
    const { paidStatus } = data

    const res = yield all(
      booking.map(booking => {
        const updatedBooking = {
          ...booking,
          paidStatus
        }

        return call(rsf.firestore.addDocument, `bookings`, updatedBooking)
      })
    )

    return res
  } else {
    const res = yield all(
      booking.map(booking => call(rsf.firestore.addDocument, `bookings`, booking))
    )

    return res
  }
}

export function* confirmSaga(type) {
  yield put(modalActions.showModal({ type }))

  const { yes } = yield race({
    yes: take(modalTypes.CONFIRM_ACTION_ATTEMPT),
    no: take(modalTypes.CANCEL_ACTION_ATTEMPT)
  })

  return !!yes
}



// WEBPACK FOOTER //
// ./src/services/sagaHelpers.js